import classNames from 'classnames'
import React, { Suspense } from 'react'
import { Outlet } from 'react-router-dom'

import { useModals } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'
import { SnackbarContainer } from '@ui/components/Snackbar/Snackbar'

import { PathHandler } from '@utils/PathHandler'
import { useWindowResize } from '@utils/domUtils'

import { ImpersonationBanner } from '@global/ImpersonationBanner/ImpersonationBanner'
import { TickmillAppBanner } from '@global/TickmillAppBanner/TickmillAppBanner'
import { AppFooter } from '@global/footer/Footer'

import { type LayoutConfig } from '@hooks/useLayoutVariant'

import { Loading } from '@shared/components/Loading/Loading'
import { TopBarAnon } from '@shared/components/Navigation/v2/TopBar/TopBarAnon'

import styles from './LayoutFull.module.scss'

interface LayoutProps {
  layout: LayoutConfig
  setCurrentPath: (path: string) => void
}

/**
 * setting 1 — minimal anonymous layout
 * topBarAnon (Logo + LanguagePicker) + Outlet
 * no sidebar, no breadcrumbs, no notifications, no footer
 */
export const LayoutMinimalAnon: React.FC<LayoutProps> = ({ layout, setCurrentPath }) => {
  const { modals } = useModals()
  const isMobile = useWindowResize()
  const isFlushContent = layout.content === 'flush'
  const isContainerContent = layout.content === 'container'
  const isSemiContent = layout.content === 'semi'
  const mainClassName = classNames(styles.main, {
    [styles.mainPadded]: !isFlushContent,
    [styles.mainFlush]: isFlushContent,
    [styles.mainInnerSemi]: isSemiContent,
  })
  const mainInnerClassName = classNames(styles.mainInner, {
    [styles.mainInnerContainer]: isContainerContent,
  })

  return (
    <div className='expander defaultBackground'>
      <ImpersonationBanner />
      <SnackbarContainer />
      <Modal.Wrapper modals={modals} />
      {isMobile && <TickmillAppBanner />}
      <TopBarAnon />
      <main id='mainContainer' className={mainClassName}>
        <div className={mainInnerClassName}>
          <Suspense fallback={<Loading isLoading showLoadingIcon />}>
            <PathHandler setCurrentPath={setCurrentPath} />
            <div className={styles.outlet}>
              <Outlet />
            </div>
          </Suspense>
        </div>
      </main>
      <AppFooter />
    </div>
  )
}
